import React from 'react';
import { withRouter } from 'react-router-dom';
import * as moment from 'moment';
import {
    Container,
    FormGroup,
    Button,
} from 'reactstrap';
import { CircularProgress } from '@mui/material';
import cls from 'classnames';
import CommonContext, { ApiRoutes, AppNavPaths, LocalizationKeys as l } from '../Common';
import {
    CollapseUnderlineHeader,
    FlexColumnCenter,
    FlexCenterRow,
    FlexStartRow,
    FormLabel,
    toasty,
} from '../common/forms/FormElements';

import { util } from '../Util';
import './TimesheetForm.scss';
import authService from '../api-authorization/AuthorizeService';
// import { TimesheetStatus } from './Timesheet';
import JobFileAttachments from '../job/JobFileAttachments';
import { RejectionNote, ClientCancelled, TimesheetStatus } from './Timesheet';
import { JobDetails } from '../job/Job';
import {
    ContractTimesheetResponsibilityType,
    TimesheetResponsibilityType,
} from '../tenant/Tenant';

export const ConfirmationStatus = {
    Unfilled: 1,
    Scheduled: 2,
    NoResponse: 3,
    Confirmed: 4,
    CancelPending: 5,
    ThresholdCancel: 6,
};

class TimesheetForm extends React.Component {
    static contextType = CommonContext;

    constructor(props) {
        super(props);

        this.timesheetItemRef = React.createRef();
        this.timesheetClientReviewRef = React.createRef();
        this.timesheetEmployeeReviewRef = React.createRef();
        this.timesheetEmployeeReviewAllRef = React.createRef();

        this.state = {
            assignment: null,
            confirmedAssignments: [],
            invalidChargeTypeIds: [],
            jobDetailsOpen: false,
            loading: true,
            notFound: false,
            timesheetOpen: false,
            timesheetClientReviewOpen: false,
            timesheetEmployeeReviewAllOpen: false,
            timesheet: {},
            timesheetId: null,
            showLinkedTimesheetModal: false,
            isSavingLinkedTimesheet: false,
        };
    }

    componentDidMount() {
        this._subscription = authService.subscribe(() => this.populateState());
        this.populateState();
    }

    onAddTimesheet = async () => {
        const { timesheet } = this.state;
        this.setState({ isSavingLinkedTimesheet: true });

        const parentTimesheet = timesheet.timesheets.find(
            (x) => x.linkedTimesheetId == null,
        );
        const result = await util.fetch.post(
            ApiRoutes.timesheet.createLinkedTimesheet(parentTimesheet.id),
        );

        if (result) {
            toasty.success('Timesheet added.');
        } else {
            let error = 'Error adding timesheet.';
            if ((result ?? {}).error) {
                error = result.error;
            }
            toasty.error(error);
        }

        this.setState({
            isSavingLinkedTimesheet: false,
            showLinkedTimesheetModal: false,
        });
        await this.refresh();
    };

    onTimesheetClientReviewClosed = async () => {
        await this.context.setFormOpened(false);

        await this.refresh();
    };

    onTimesheetEmployeeReviewClosed = async () => {
        await this.context.setFormOpened(false);

        await this.refresh();
    };

    onTimesheetEmployeeReviewAllClosed = async () => {
        await this.context.setFormOpened(false);

        const { timesheet } = this.state;
        var returnUrl = '';
        if (timesheet?.jobStartTime != null) {
            var j = timesheet?.jobStartTime.indexOf(' ');
            var dt = timesheet?.jobStartTime.substring(0, j);
            returnUrl = '/dailyDrivingLog/' + moment(dt).format('YYYYMMDD');
        } 

        if (returnUrl.length > 0)
            window.location.replace(returnUrl);
    };

    onTimesheetItemClosed = async () => {
        await this.context.setFormOpened(false);

        await this.refresh();
    };

    async openTimesheet(id) {
        if (id) {
            await this.context.setFormOpened(true);
            this.timesheetItemRef.current.open(id);
        }
    }

    async openTimesheetClientReview(id) {
    // This should only open up once all billable charges on TS have time or NA.
    // It can also be opened up to view once its been filled out to review if needed(basically same as above).

        if (id) {
            await this.context.setFormOpened(true);
            this.timesheetClientReviewRef.current.open(id);
        }
    }

    async openTimesheetEmployeeReview(timesheet) {
        if (timesheet) {
            await this.context.setFormOpened(true);
            this.timesheetEmployeeReviewRef.current.open(timesheet.id, timesheet.employeeId);
        }
    }

    async openTimesheetEmployeeReviewAll() {
        const { timesheetId } = this.state;

        await this.context.setFormOpened(true);
        this.timesheetEmployeeReviewAllRef.current.open(timesheetId);
    }

    populateState = async () => {
        const isAuthenticated = await authService.isAuthenticated();
        if (isAuthenticated) {
            const timesheetId = this.props.match.params.id;
            const assignmentId = this.props.location.state !== undefined
                && Object.prototype.hasOwnProperty.call(this.props.location.state, 'assignmentId')
                ? this.props.location.state.assignmentId
                : 0;

            const strings = await util.l10n.getStrings([l.crewLead]);
            this.setState({ strings });

            if (!timesheetId) {
                this.setState({ notFound: true, loading: false });
                return;
            }

            // Timesheet
            const timesheet = await util.fetch.js(
                ApiRoutes.timesheet.overview(timesheetId),
            );
            if (!timesheet) {
                this.setState({ notFound: true, loading: false });
                return;
            }

            timesheet.clientReviewPopoverOpen = false;
            timesheet.crewReviewPopoverOpen = false;

            // Assignment
            let enforceCrewLeadTS = false;
            let assignment = null;

            if (assignmentId > 0) {
                assignment = await util.fetch.js(
                    ApiRoutes.jobAssignment.byId(assignmentId),
                );
                if (assignment) {
                    enforceCrewLeadTS = this.context.tenant.tenantSettings
                        .timesheetResponsibilityType
                            === TimesheetResponsibilityType.CrewLead
                        || (this.context.tenant.tenantSettings
                            .timesheetResponsibilityType
                            === TimesheetResponsibilityType.Contract
                            && assignment.contractTimesheetResponsibilityTypeId
                                === ContractTimesheetResponsibilityType.CrewLead);
                }
            }

            this.setState({
                timesheet,
                assignment,
                jobDetailsOpen: true,
                timesheetId,
                loading: false,
                enforceCrewLeadTimesheet: enforceCrewLeadTS,
            });
        }
    };

    refresh = async () => {
        const { timesheetId } = this.state;
        const timesheet = await util.fetch.js(
            ApiRoutes.timesheet.overview(timesheetId),
        );

        if (!timesheet) this.setState({ notFound: true, loading: false });

        // create review items for the signature grids

        this.setState({
            timesheet,
            jobDetailsOpen: true,
            loading: false,
        });
    };

    onSubmitTickets = async () => {
        const { timesheetId } = this.state;

        await this.context.setFormOpened(true);
        this.timesheetEmployeeReviewAllRef.current.open(timesheetId);

        this.setState({ timesheetEmployeeReviewAllOpen: true });
    }

    toggleLinkedTimesheetModal = () => {
        const { showLinkedTimesheetModal } = this.state;
        this.setState({ showLinkedTimesheetModal: !showLinkedTimesheetModal });
    };

    onTicketsClicked = (event) => {
        const { timesheetId } = this.state;
        this.props.history.push(`${AppNavPaths.TimesheetDetails}/${timesheetId}`);
    };

    render() {
        const {
            timesheet,
            jobDetailsOpen,
            loading,
            notFound,
        } = { ...this.state };

        if (!!loading || !(this.context.tenant ?? {}).tenantSettings) {
            return (
                <div className="confirmationContainer h-100 pt-2 d-flex flex-row align-items-center justify-content-center">
                    <CircularProgress
                        color="secondary"
                        variant="indeterminate"
                    />
                </div>
            );
        }
        const { tenantSettings } = { ...this.context.tenant };

        const showContractDescriptionInTimesheetView = Boolean(
            tenantSettings
                    && tenantSettings.showContractDescriptionInTimesheetView,
        );

        const jobDetailsHeader = showContractDescriptionInTimesheetView
                    && timesheet
                    && timesheet.contractDescription
            ? `Order Summary - ${timesheet.contractDescription}`
            : 'Order Summary';

        var returnUrl = '';
        if (timesheet?.jobStartTime != null) {
            var j = timesheet?.jobStartTime.indexOf(' ');
            var dt = timesheet?.jobStartTime.substring(0, j);
            returnUrl = '/dailyDrivingLog/' + moment(dt).format('YYYYMMDD');
        } 

        const jobDate = timesheet?.jobStartTime.substring(0, timesheet?.jobStartTime.indexOf(' '));

        return (
            <>
                <div
                    className={cls('confirmationContainer h-100 pt-2', {
                        'site-form-disabled': !!this.context.formIsOpen,
                    })}
                >
                    <Container size="sm">
                        {!!notFound && (
                            <h4 className="text-center">
                Unable to locate the requested resource.
                            </h4>
                        )}
                        {!!timesheet && (
                            <>
                                {(timesheet ?? {}).isClientCancelled && (
                                    <FlexColumnCenter className="pb-3">
                                        <ClientCancelled />
                                    </FlexColumnCenter>
                                )}
                                {(timesheet ?? {}).timesheetStatusId
                                        === TimesheetStatus.Rejected && (
                                    <FlexColumnCenter className="pb-3">
                                        <RejectionNote
                                            note={
                                                timesheet.lastRejectionNote
                                            }
                                        />
                                    </FlexColumnCenter>
                                )}
                                <a style={{ paddingLeft: '0px' }} href={returnUrl} className="btn site-link">&lt;&nbsp;Previous</a>
                                <CollapseUnderlineHeader
                                    headerText={jobDetailsHeader}
                                    isOpen={jobDetailsOpen}
                                    toggleCollapse={() => this.setState({
                                        jobDetailsOpen: !jobDetailsOpen,
                                    })}
                                >
                                    <JobDetails
                                        customerName={timesheet?.customerName}
                                        subcontractorName={timesheet?.subcontractorName}
                                        foremanName={timesheet?.foremanName}
                                        foremanPhone={timesheet?.foremanPhone}
                                        jobNotes={timesheet?.jobNotes}
                                        jobNumber={timesheet?.jobNumber}
                                        jobStartTime={jobDate}
                                        lastRejectionNote={timesheet?.lastRejectionNote}
                                        workOrderNumber={timesheet?.workOrderNumber}
                                        isEmergency={timesheet?.isEmergency}
                                        materials={timesheet?.materials}
                                        isTimesheetForm
                                        isTimesheet
                                        isPayroll={false}
                                        isBilling={false}
                                        tenantSettings={tenantSettings}
                                        jobName={timesheet?.contractDescription}
                                        hours={timesheet?.hours}
                                        tonnage={timesheet?.tonnage}
                                        loads={timesheet?.loads}
                                    />

                                    <div className="locationDetails">
                                        <FormGroup>
                                            <FormLabel
                                                htmlFor="meetingLocation"
                                                text="Haul From"
                                                className="bottom-border"
                                            />
                                            <FlexStartRow>
                                                {Boolean(timesheet?.meetingLocationUrlFormatted) && (
                                                    <a
                                                        rel="noopener noreferrer"
                                                        className="text-white mr-2 btn btn-sm btn-success p-1"
                                                        title="Open in Google Maps"
                                                        href={`https://maps.google.com?q=${timesheet.meetingLocationUrlFormatted}`}
                                                        target="_blank"
                                                    >
                                                        <i className="fa fa-map-marked-alt" />
                                                    </a>
                                                )}
                                                <span
                                                    id="meetingLocation"
                                                    name="meetingLocation"
                                                >
                                                    {timesheet.meetingLocation ?? '-'}
                                                </span>
                                            </FlexStartRow>
                                        </FormGroup>
                                        <FormGroup>
                                            <FormLabel
                                                htmlFor="jobLocation"
                                                text="Haul To"
                                                className="bottom-border"
                                            />
                                            <FlexStartRow>
                                                {Boolean(timesheet?.jobLocationUrlFormatted) && (
                                                    <a
                                                        rel="noopener noreferrer"
                                                        className="text-white mr-2 btn btn-sm btn-success p-1"
                                                        title="Open in Google Maps"
                                                        href={`https://maps.google.com?q=${timesheet.jobLocationUrlFormatted}`}
                                                        target="_blank"
                                                    >
                                                        <i className="fa fa-map-marked-alt" />
                                                    </a>
                                                )}
                                                <span
                                                    id="jobLocation"
                                                    name="jobLocation"
                                                >
                                                    {timesheet.jobLocation ?? '-'}
                                                </span>
                                            </FlexStartRow>
                                        </FormGroup>
                                        <FormGroup>
                                            <FormLabel
                                                htmlFor="usCounty"
                                                text="Driving Instructions"
                                                className="bottom-border"
                                            />

                                            <textarea style={{ borderColor: 'lightblue', borderWidth: '2px', padding: '3px' }} rows="6" readOnly="true">
                                                {timesheet.jobNotes ?? ''}
                                            </textarea>

                                        </FormGroup>   
                                    </div>

                                    {!!tenantSettings.jobUploadsEnabled
                                            && !!(timesheet.jobUploads ?? [])
                                                .length && (
                                        <FormGroup className="mt-2">
                                            <FormLabel
                                                style={{
                                                    fontSize: '14px',
                                                }}
                                                text="File Attachments"
                                                className="bottom-border"
                                            />
                                            <FlexStartRow className="pl-3">
                                                <JobFileAttachments
                                                    viewOnly
                                                    className="d-flex flex-row align-items-center flex-wrap"
                                                    job={{
                                                        id: timesheet.jobId,
                                                        uploads: timesheet.jobUploads,
                                                    }}
                                                />
                                            </FlexStartRow>
                                        </FormGroup>
                                    )}
                                </CollapseUnderlineHeader>
                            </>
                        )}
                        
                    </Container>
                </div>

                <FlexCenterRow className="userActions p-1">
                    <Button
                        type="button"
                        color="success"
                        id="addChargesButton"
                        onClick={(e) => this.onTicketsClicked(e)}
                    >
                        Tickets...
                    </Button>
                </FlexCenterRow>

            </>
        );
    }
}

export default withRouter(TimesheetForm);
